import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useSupabase } from "../hooks/useSupabase";
import { useCategories } from "../hooks/useCategories";
import { useProducts } from "../hooks/useSupabase";
import logo1 from "../media/VanBelle Logo Light.webp";
import UserAvatar from "./UserAvatar";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const { getTotalItems } = useCart();
  const { user, signOut } = useSupabase();
  const { categories } = useCategories();
  const { products } = useProducts();
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';
  const dropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);

  // Get categories that have at least 1 active product
  const categoriesWithProducts = categories.filter(category => 
    products.some(p => p.is_active !== false && p.category_id === category.id)
  );

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setCategoryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Product search. On the shop page results update as you type (via ?search=);
  // on other pages pressing Enter opens the shop with the results.
  const isShopPage = location.pathname === '/shop';
  const searchParam = isShopPage ? new URLSearchParams(location.search).get('search') || '' : '';

  useEffect(() => {
    // Keep the box in sync with the address (e.g. "clear search" on the shop page),
    // and close it when leaving the shop
    setSearchText(searchParam);
    if (searchParam) setSearchOpen(true);
    else if (!isShopPage) setSearchOpen(false);
  }, [searchParam, isShopPage]);

  const shopSearchUrl = (text) => (text.trim() ? `/shop?search=${encodeURIComponent(text.trim())}` : '/shop');

  const handleSearchChange = (e) => {
    const text = e.target.value;
    setSearchText(text);
    if (isShopPage) navigate(shopSearchUrl(text), { replace: true });
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!isShopPage) navigate(shopSearchUrl(searchText));
    e.target.querySelector('input')?.blur(); // hide the phone keyboard
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchText('');
    if (isShopPage && searchParam) navigate('/shop', { replace: true });
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') closeSearch();
  };

  // Close an empty search box when it loses focus
  const handleSearchBlur = () => {
    if (!searchText.trim()) setSearchOpen(false);
  };

  const searchInputProps = {
    type: 'text',
    inputMode: 'search',
    enterKeyHint: 'search',
    value: searchText,
    onChange: handleSearchChange,
    onKeyDown: handleSearchKeyDown,
    onBlur: handleSearchBlur,
    placeholder: 'Search chocolates...',
    'aria-label': 'Search products',
    autoFocus: true,
    className: 'w-full bg-neutral-800 border border-yellow-100/30 rounded-full px-4 py-1.5 text-sm text-yellow-50 placeholder-yellow-100/50 focus:outline-none focus:border-yellow-300 shadow-lg',
  };

  const toggleNavbar = () => {
    setIsOpen(!isOpen);
  };

  // Side menu (below xl): close on navigation, close with Escape, and stop the page scrolling behind it
  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e) => e.key === 'Escape' && setIsOpen(false);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isOpen]);

  // Search from the side menu: runs on Enter (the menu covers the page, so no live results)
  const handleMenuSearchSubmit = (e) => {
    e.preventDefault();
    navigate(shopSearchUrl(searchText), { replace: isShopPage });
    setIsOpen(false);
  };

  const goHomeSection = (sectionId) => {
    navigate('/');
    setIsOpen(false);
    setTimeout(() => {
      document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  const categoryPath = (category) => `/shop/category/${category.name.toLowerCase().replace(/\s+/g, '-')}`;

  const handleLogout = async () => {
    await signOut();
  };

  return (
    <>
    <nav className="fixed top-0 left-0 w-full z-30 bg-black/70 backdrop-blur-md shadow-lg">
      {/* Always one row: logo on the left, everything else on the right.
          Below xl the menu links and search live in the side menu (hamburger). */}
      <div className="max-w-screen-xl flex flex-nowrap items-center justify-between gap-4 mx-auto px-4 md:px-6 py-3">
        <button onClick={() => navigate('/')} className="flex-shrink-0 flex items-center bg-transparent border-0 p-0 cursor-pointer">
          <img src={logo1} className="h-7 sm:h-8 md:h-12 w-auto" alt="Van Belle Chocolatier Logo - Premium Belgian Chocolate Lebanon" fetchpriority="high" />
        </button>
        <div className="flex xl:order-2 flex-shrink-0 items-center space-x-1 md:space-x-2">
          {/* Search - large screens: box floats over the menu links, left of the icon, so nothing shifts */}
          <div className="relative hidden xl:flex items-center">
          {searchOpen && (
            <form onSubmit={handleSearchSubmit} className="absolute right-full mr-2 top-1/2 -translate-y-1/2 w-72" role="search">
              <input {...searchInputProps} />
            </form>
          )}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()} // keep focus in the box so blur doesn't close it first
            onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}
            className="p-2 text-yellow-100 hover:text-yellow-300 transition-colors bg-transparent border-0 cursor-pointer"
            title={searchOpen ? 'Close search' : 'Search'}
            aria-label={searchOpen ? 'Close search' : 'Search products'}
            aria-expanded={searchOpen}
          >
            {searchOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            )}
          </button>
          </div>

          {/* Cart Icon */}
          <button
            onClick={() => navigate('/cart')}
            className="relative p-1.5 md:p-2 text-yellow-100 hover:text-yellow-300 transition-colors bg-transparent border-0 cursor-pointer"
            title="Shopping Cart"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            {getTotalItems() > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                {getTotalItems()}
              </span>
            )}
          </button>

          {/* Authentication - Only show when NOT on home page */}
          {!isHomePage && (
            <div className="relative">
              {user ? (
                // User is logged in - show avatar dropdown
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center space-x-2 p-1.5 md:p-2 text-yellow-100 hover:text-yellow-300 transition-colors"
                    title="Account"
                  >
                    <UserAvatar user={user} size={32} className="text-sm" />
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white/95 backdrop-blur-sm rounded-xl shadow-2xl border border-amber-100 py-2 z-50">
                      <div className="px-4 py-3 border-b border-amber-100">
                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Signed in as</p>
                        <p className="text-sm text-gray-800 font-medium truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={() => {
                          navigate('/profile');
                          setDropdownOpen(false);
                        }}
                        className="flex items-center w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile
                      </button>
                      <button
                        onClick={() => {
                          handleLogout();
                          setDropdownOpen(false);
                        }}
                        className="flex items-center w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-amber-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-3 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                // User not logged in - show login button
                <button
                  onClick={() => navigate('/login')}
                  className="p-1.5 md:p-2 text-yellow-100 hover:text-yellow-300 transition-colors"
                  title="Sign In"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                </button>
              )}
            </div>
          )}
   
          {/* Shop Button - Always show on Desktop */}
          <button
            onClick={() => navigate('/shop')}
            className="hidden xl:inline-flex items-center px-5 py-2 rounded-full bg-yellow-900 text-yellow-100 font-semibold hover:bg-yellow-800 transition"
          >
            Shop
          </button>

          <button
            type="button"
            onClick={toggleNavbar}
            className="inline-flex items-center p-1.5 w-9 h-9 justify-center text-yellow-100 rounded-lg xl:hidden hover:bg-yellow-900/30 focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-200"
            aria-expanded={isOpen ? "true" : "false"}
            aria-controls="side-menu"
          >
            <span className="sr-only">Open main menu</span>
            <svg
              className="w-6 h-6"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          </button>
        </div>
        {/* Menu links - large screens only (xl and up), where they fit on the same row */}
        <div className="hidden xl:block xl:order-1" id="navbar-cta">
          <ul className="flex flex-row space-x-2 items-center">
            <li>
              <button onClick={() => goHomeSection('main')} className="block py-2 px-3 whitespace-nowrap text-yellow-100 font-medium rounded hover:text-yellow-300 transition" aria-current="page">
                Home
              </button>
            </li>
            <li>
              <button onClick={() => goHomeSection('latest')} className="block py-2 px-3 whitespace-nowrap text-yellow-100 font-medium rounded hover:text-yellow-300 transition">
                Our Latest
              </button>
            </li>

            {/* Categories Dropdown */}
            {!isHomePage && (
              <li className="relative" ref={categoryDropdownRef}>
                <button
                  onMouseEnter={() => setCategoryDropdownOpen(true)}
                  onClick={() => setCategoryDropdownOpen(!categoryDropdownOpen)}
                  className="flex items-center py-2 px-3 whitespace-nowrap text-yellow-100 font-medium rounded hover:text-yellow-300 transition"
                >
                  Categories
                  <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {categoryDropdownOpen && (
                  <div
                    className="absolute left-0 mt-2 w-56 bg-white rounded-lg shadow-xl py-2 z-50"
                    onMouseLeave={() => setCategoryDropdownOpen(false)}
                  >
                    <button
                      onClick={() => {
                        navigate('/shop');
                        setCategoryDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 transition-colors font-medium"
                    >
                      All Products
                    </button>
                    <div className="border-t border-gray-200 my-1"></div>
                    {categoriesWithProducts.map(category => (
                      <button
                        key={category.id}
                        onClick={() => {
                          navigate(categoryPath(category));
                          setCategoryDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-amber-50 transition-colors"
                      >
                        {category.name}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            )}

            <li>
              <button onClick={() => navigate('/about')} className="block py-2 px-3 whitespace-nowrap text-yellow-100 font-medium rounded hover:text-yellow-300 transition">
                About Us
              </button>
            </li>
            <li>
              <a href="https://maps.app.goo.gl/7PQF3VH4TTaEFaKP6" target="_blank" rel="noopener noreferrer" className="block py-2 px-3 whitespace-nowrap text-yellow-100 font-medium rounded hover:text-yellow-300 transition">
                Visit Us
              </a>
            </li>
            <li>
              <a href="https://wa.me/96176919011" target="_blank" rel="noopener noreferrer" className="block py-2 px-3 whitespace-nowrap text-yellow-100 font-medium rounded hover:text-yellow-300 transition">
                Contact
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>

    {/* Side menu - below xl. Rendered outside <nav>: its backdrop-blur would otherwise
        make these fixed elements position relative to the bar instead of the screen. */}
    <div
      className={`xl:hidden fixed inset-0 z-40 bg-black/60 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      onClick={() => setIsOpen(false)}
      aria-hidden="true"
    />
    <aside
      id="side-menu"
      className={`xl:hidden fixed top-0 right-0 z-50 h-full w-80 max-w-[85vw] bg-neutral-900 shadow-2xl flex flex-col transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full invisible'}`}
      aria-label="Menu"
      aria-hidden={!isOpen}
    >
      <div className="flex items-center justify-between px-4 py-4 border-b border-yellow-100/10">
        <span className="text-yellow-100 font-semibold tracking-wide">Menu</span>
        <button
          type="button"
          onClick={() => setIsOpen(false)}
          className="p-2 text-yellow-100 hover:text-yellow-300 rounded-lg"
          aria-label="Close menu"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-4">
        {/* Search */}
        <form onSubmit={handleMenuSearchSubmit} className="relative mb-4 px-1" role="search">
          <input
            type="text"
            inputMode="search"
            enterKeyHint="search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder="Search chocolates..."
            aria-label="Search products"
            className="w-full bg-neutral-800 border border-yellow-100/30 rounded-full pl-10 pr-4 py-2.5 text-sm text-yellow-50 placeholder-yellow-100/50 focus:outline-none focus:border-yellow-300"
          />
          <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-yellow-100/60 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </form>

        <ul className="space-y-1">
          <li>
            <button onClick={() => goHomeSection('main')} className="block w-full text-left py-3 px-4 text-yellow-100 font-medium rounded-lg hover:bg-yellow-900/50 transition">Home</button>
          </li>
          <li>
            <button onClick={() => goHomeSection('latest')} className="block w-full text-left py-3 px-4 text-yellow-100 font-medium rounded-lg hover:bg-yellow-900/50 transition">Our Latest</button>
          </li>
          <li>
            <button onClick={() => { navigate('/shop'); setIsOpen(false); }} className="block w-full text-left py-3 px-4 text-yellow-100 font-medium rounded-lg hover:bg-yellow-900/50 transition">
              {isHomePage ? 'Shop' : 'All Products'}
            </button>
          </li>
          {!isHomePage && categoriesWithProducts.length > 0 && (
            <li className="pt-3">
              <p className="px-4 pb-1 text-[11px] uppercase tracking-wider text-yellow-100/50">Categories</p>
              <ul className="space-y-1">
                {categoriesWithProducts.map(category => (
                  <li key={category.id}>
                    <button
                      onClick={() => { navigate(categoryPath(category)); setIsOpen(false); }}
                      className="block w-full text-left py-3 px-4 text-yellow-100 font-medium rounded-lg hover:bg-yellow-900/50 transition pl-6 text-yellow-100/90"
                    >
                      {category.name}
                    </button>
                  </li>
                ))}
              </ul>
            </li>
          )}
          <li className="pt-3 border-t border-yellow-100/10 mt-3">
            <button onClick={() => { navigate('/about'); setIsOpen(false); }} className="block w-full text-left py-3 px-4 text-yellow-100 font-medium rounded-lg hover:bg-yellow-900/50 transition">
              About Us
            </button>
          </li>
          <li>
            <a href="https://maps.app.goo.gl/7PQF3VH4TTaEFaKP6" target="_blank" rel="noopener noreferrer" className="block w-full text-left py-3 px-4 text-yellow-100 font-medium rounded-lg hover:bg-yellow-900/50 transition">
              Visit Us
            </a>
          </li>
          <li>
            <a href="https://wa.me/96176919011" target="_blank" rel="noopener noreferrer" className="block w-full text-left py-3 px-4 text-yellow-100 font-medium rounded-lg hover:bg-yellow-900/50 transition">
              Contact
            </a>
          </li>
        </ul>
      </div>
    </aside>
    </>
  );
};

export default Navbar;
